import { type UserVoucherRes } from '@/api/gen/_model/userVoucherRes';
import { useGetMyProfile } from '@/api/gen/quiz-user/quiz-user.gen';
import { useGetMyVouchers } from '@/api/gen/vouchers/vouchers.gen';
import { useUser } from '@/lib/auth';
import { isPhoneTrialUpsellEligible } from '@/shared/features/phone-verification/utils';

/** 판정에 필요한 이용권만 받는다 — 이미 쓴 이용권은 조회하지 않는다. */
const UNUSED_VOUCHERS_PARAMS = { usageStatus: 'UNUSED' } as const;
const PHONE_TRIAL_CAMPAIGN_TYPE = 'PHONE_VERIFICATION_REWARD';

/**
 * FREE 사용자의 헤더 CTA 상태 (Figma 28913:53540).
 *
 * 기존에는 플랜 등급만 표시했지만("FREE 플랜 이용 중"), 이제는 **지금 무엇을 할 수 있는지**를
 * 보여 준다. 같은 FREE 라도 할 수 있는 일이 셋으로 갈린다.
 */
export type FreePlanHeaderState =
  /** 핸무체 이용권이 있다 → 체험 시작 다이얼로그 */
  | { type: 'trial-voucher'; voucher: UserVoucherRes }
  /** 휴대폰 인증만 하면 체험 이용권을 받는다 → 인증 페이지 */
  | { type: 'phone-trial' }
  /** 남은 길은 결제뿐이다 → 요금제 안내 */
  | { type: 'upgrade' };

/**
 * FREE 사용자에게만 조회한다. 헤더는 전 페이지에 깔리므로 유료 사용자까지 조회하면
 * 아무도 쓰지 않을 데이터를 매 페이지 받아 오게 된다.
 *
 * `fetchQuery`(staleTime 0)를 쓰는 페이월과 달리 여기서는 일반 쿼리를 쓴다.
 * 헤더는 상시 노출이라 페이지마다 새로 받으면 낭비고, 캐시가 조금 낡아도
 * 라벨이 한 박자 늦게 바뀔 뿐 잘못된 동작으로 이어지지 않는다.
 */
export const useFreePlanHeaderState = (): FreePlanHeaderState => {
  const { user, isAuthenticated } = useUser();
  const isFree = user?.planType === 'FREE';
  const enabled = isAuthenticated && isFree;

  // 전역 쿼리 기본값은 throwOnError: true 라, 실패하면 컴포넌트가 throw 하고
  // GlobalErrorBoundary 가 잡는다. 이 훅은 **모든 페이지에 깔리는 헤더**에서 도므로
  // 그대로 두면 라벨 하나를 정하려는 조회가 앱 전체를 내린다.
  //
  // 실패는 값의 부재로 흡수한다 — usableVoucher 가 없고 인증 여부를 모르면
  // 아래 판정이 자연스럽게 'upgrade'(기본 상태)로 떨어진다. 라벨만 보수적으로 나온다.
  //
  // meta.ignoreError 는 쓰지 않는다. 전역 onError 는 401 일 때 세션을 정리하고
  // 로그인으로 보내는데, 그건 계속 동작해야 한다. 막는 건 렌더로의 throw 뿐이다.
  const { data: vouchersResponse } = useGetMyVouchers(UNUSED_VOUCHERS_PARAMS, {
    query: { enabled, throwOnError: false },
  });
  const { data: profileResponse } = useGetMyProfile({
    query: { enabled, throwOnError: false },
  });

  const vouchers = vouchersResponse?.data;
  const trialVoucher = vouchers?.canUseVouchers
    ? vouchers.items.find(
        voucher =>
          voucher.canUseNow &&
          voucher.systemCampaignType === PHONE_TRIAL_CAMPAIGN_TYPE,
      )
    : undefined;

  // 체험 CTA는 핸무체 이용권에만 쓴다. 일반 이용권은 기능별 페이월에서 안내한다.
  if (trialVoucher) {
    return { type: 'trial-voucher', voucher: trialVoucher };
  }

  if (
    isPhoneTrialUpsellEligible({
      countryCode: user?.countryCode,
      isPhoneVerified: Boolean(profileResponse?.data.verifiedPhoneNumber),
      planType: user?.planType,
      userType: user?.type,
    })
  ) {
    return { type: 'phone-trial' };
  }

  return { type: 'upgrade' };
};
